import ReactCodeMirror, {
  EditorSelection,
  keymap,
  placeholder,
} from "@uiw/react-codemirror";
import { indentWithTab, indentSelection } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";

import { Typography } from "@mui/material";
import { defaultScriptValue } from "@/utils/utils";
import { autocompletion } from "@codemirror/autocomplete";

const variableCompletionsGenerator = (variables) => {
  if (!variables) {
    return () => null;
  }
  const completionMap = variables.map((variable) => {
    return { label: variable, type: "variable" };
  });

  const completions = (context) => {
    // Basic completion conditions
    let word = context.matchBefore(/[a-zA-Z\d\.]*/);
    if (word.from === word.to && !context.explicit) {
      return null;
    }

    return {
      from: word.from,
      options: completionMap,
    };
  };

  return completions;
};

const anonymousFunctionShortcut = (context) => {
  const isAnon = /^\(\(\) ?=> ?{[\s\S]*}\)\(\);?$/.test(context.state.doc);
  if (isAnon) {
    return null;
  }
  let word = context.matchBefore(/[\w]*/);
  if (word.from === word.to) {
    return null;
  }

  return {
    from: word.from,
    options: [
      {
        label: "anonymous function",
        type: "keyword",
        apply: (view, completion, from, to) => {
          // Wrap existing code between anonymous function brackets
          view.dispatch([
            view.state.update(
              {
                changes: {
                  from: from,
                  to: to,
                },
              },
              {
                changes: { from: 0, insert: "(() => {\n" },
              },
              {
                changes: {
                  from: view.state.doc.length,
                  insert: "\nreturn;\n})();",
                },
              }
            ),
          ]);
          // Select everything and correct indenting
          const prevAnchor = view.state.selection.main.anchor + (to - from);
          view.state.selection = EditorSelection.single(
            0,
            view.state.doc.length
          );
          indentSelection({
            state: view.state,
            dispatch: view.dispatch,
          });
          // Approximately go back to where cursor was previously
          view.state.selection = EditorSelection.single(prevAnchor);
        },
      },
    ],
  };
};

const ScriptEditor = ({ value, onChange, variableCompletions, onCreate, height, minHeight, maxHeight }) => {
  const jsInstance = javascript();
  const variableCompletionExtension = jsInstance.language.data.of({
    autocomplete: variableCompletionsGenerator(variableCompletions),
  });
  const anonCompletion = jsInstance.language.data.of({
    autocomplete: anonymousFunctionShortcut,
  });

  const editor = (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Typography>JS Editor</Typography>
      <ReactCodeMirror
        value={value}
        onChange={onChange}
        onCreateEditor={onCreate}
        height={height ?? undefined}
        minHeight={minHeight}
        maxHeight={maxHeight}
        style={{ margin: "10px", border: "1px solid" }}
        extensions={[
          jsInstance,
          keymap.of([indentWithTab]), // This will lock tab focus https://codemirror.net/examples/tab can also toggle using setTabFocusMode()
          placeholder(defaultScriptValue),
          variableCompletionExtension,
          anonCompletion,
          autocompletion(),
        ]}
      />
    </div>
  );

  return editor;
};

export default ScriptEditor;
